import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSetAtom } from "jotai";
import {
  ConfirmPaymentRequestBody,
  ConfirmPaymentResponse,
  TransactionStatus,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import {
  ROUTES,
  TopupOrigins,
  usePlayGameMetaDescription,
} from "../../../utils/constants";
import PaymentStatusTemplate, {
  PaymentStatusType,
} from "../../../components/Payments/PaymentStatusTemplate/PaymentStatusTemplate";
import { handleConfirmPayment } from "../../../api-clients/walletsPaymentsClient";
import Logger from "../../../utils/logger";
import {
  deleteStoredTopupOrigin,
  getStoredTopupOrigin,
} from "../../../utils/storage";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../../utils/renderHeaders";
import LoadingSpinner from "../../../components/Payments/LoadingSpinner/LoadingSpinner";
import {
  paymentStatusAtom,
  PaymentStatuses,
  topupInfoAtom,
} from "../../../jotai/atoms/topup";

enum PaymentConfirmationUrlParams {
  REFERENCE = "reference",
  PAYER_ID = "PayerID",
  TOKEN = "token",
  PAYMENT_ID = "paymentId",
}

function PaymentConfirmation() {
  const navigate = useNavigate();
  const { provider } = useParams();
  const [queryParams] = useSearchParams();
  const setTopupInfo = useSetAtom(topupInfoAtom);
  const setPaymentStatus = useSetAtom(paymentStatusAtom);
  const metaDescriptions = usePlayGameMetaDescription();
  const { t } = useTranslation();

  const handlePaymentConfirmationResult = useCallback(
    (status: PaymentStatuses, paymentResponse?: ConfirmPaymentResponse) => {
      const topupOrigin = getStoredTopupOrigin();
      let route: string = paymentResponse?.isFirstTime
        ? ROUTES.WALLET.PAYMENT_SUCCESSFUL_FIRST_MOBILE
        : ROUTES.WALLET.PAYMENT_SUCCESSFUL_RETURN_MOBILE;

      switch (topupOrigin) {
        case TopupOrigins.WEB:
          route = ROUTES.WALLET.PAYMENT_STATUS;
          break;

        default:
          if (status !== PaymentStatuses.SUCCESSFUL) {
            route =
              status === PaymentStatuses.PENDING
                ? ROUTES.WALLET.PAYMENT_PENDING_MOBILE
                : ROUTES.WALLET.PAYMENT_FAILED_MOBILE;
          }
      }

      if (status === PaymentStatuses.SUCCESSFUL && paymentResponse) {
        setTopupInfo((prevInfo) => ({
          ...prevInfo,
          ...paymentResponse,
          provider,
        }));
      }
      setPaymentStatus(status);
      deleteStoredTopupOrigin();
      navigate(route);
    },
    [provider, navigate, setPaymentStatus, setTopupInfo],
  );

  const confirmPayment = useCallback(
    async (provider: string, paymentParams: ConfirmPaymentRequestBody) => {
      try {
        const paymentResponse = await handleConfirmPayment(
          provider,
          paymentParams,
        );

        if (paymentResponse?.success) {
          handlePaymentConfirmationResult(
            PaymentStatuses.SUCCESSFUL,
            paymentResponse,
          );
          return;
        }

        if (
          paymentResponse?.status === TransactionStatus.REJECTED ||
          paymentResponse?.status === TransactionStatus.FAILED
        ) {
          handlePaymentConfirmationResult(PaymentStatuses.FAILED);
          return;
        }

        handlePaymentConfirmationResult(PaymentStatuses.PENDING);
      } catch (e) {
        Logger.log(e);
        handlePaymentConfirmationResult(PaymentStatuses.PENDING);
      }
    },
    [handlePaymentConfirmationResult],
  );

  useEffect(() => {
    const getPaymentParams = () => {
      const reference =
        queryParams.get(PaymentConfirmationUrlParams.REFERENCE) || "";
      const payerId =
        queryParams.get(PaymentConfirmationUrlParams.PAYER_ID) ?? undefined;
      const token =
        queryParams.get(PaymentConfirmationUrlParams.TOKEN) ?? undefined;
      const paymentId =
        queryParams.get(PaymentConfirmationUrlParams.PAYMENT_ID) ?? undefined;
      return { reference, payerId, token, paymentId };
    };

    const paymentParams = getPaymentParams();
    confirmPayment(provider as string, paymentParams);
  }, [provider, queryParams, confirmPayment]);

  return (
    <>
      {renderHeaders({
        title: t("metadata:payment_confirmation_hyper_gaming_app"),
        description: metaDescriptions.generic,
        ogTitle: extractOgTitleFromMainTitle(
          t("metadata:payment_confirmation_hyper_gaming_app"),
        ),
      })}

      <PaymentStatusTemplate
        type={PaymentStatusType.LOADING}
        title="Confirming Payment"
        message="Your payment is being confirmed..."
        icon={<LoadingSpinner />}
      />
    </>
  );
}

export default PaymentConfirmation;
